/**
 * Turns room state + layout into (a) the tiles to render and (b) the exact
 * subscription plan to put on the wire. This is where the EX44 capacity model
 * becomes code: pagination, the tile-count clamp, speaker promotion and
 * top-N audio all meet here.
 */
import { MEDIA_LIMITS, QUALITY_PROFILES, type MediaLayer, type QualityProfile } from '@cyphex/types';
import type { MediaParticipant, SubscriptionPlan } from './contract';
import { clampLayer, wireCostBps, type TileRole } from './densityPolicy';
import { SHARE_AUDIO_BITRATE } from './shareBudget';
import { orderTiles } from './tileOrder';
import { audioSubscriptions, roleForTile } from './tileRoles';

export type MeetingLayout = 'gallery' | 'speaker' | 'presentation';

export interface TileView {
  readonly participant: MediaParticipant;
  readonly role: TileRole;
  /** null = video intentionally not forwarded (off-page, hidden tab, audio-only). */
  readonly layer: MediaLayer | null;
}

export interface TilePlanInput {
  readonly participants: readonly MediaParticipant[];
  readonly activeSpeakerIds: readonly string[];
  readonly localId: string;
  readonly screenShareOwnerId: string | null;
  readonly pinnedId: string | null;
  readonly layout: MeetingLayout;
  readonly profile: QualityProfile;
  readonly page: number;
  readonly tileCssWidth: number;
  readonly devicePixelRatio: number;
  /** Tab hidden past the grace period, or the user chose audio-only. */
  readonly videoSuspended: boolean;
}

export interface TilePlan {
  readonly tiles: readonly TileView[];
  readonly page: number;
  readonly pageCount: number;
  readonly pageSize: number;
  readonly plan: SubscriptionPlan;
  /** What this plan costs the server, bps. Shown in the diagnostics card. */
  readonly estimatedDownlinkBps: number;
}

const AUDIO_WIRE_BPS = 55_000;

const pageSizeFor = (layout: MeetingLayout): number =>
  layout === 'gallery' ? MEDIA_LIMITS.MAX_VISIBLE_TILES : 5;

export function buildTilePlan(input: TilePlanInput): TilePlan {
  const pageSize = pageSizeFor(input.layout);
  const ordered = orderTiles(input.participants, {
    activeSpeakerIds: input.activeSpeakerIds,
    pinnedId: input.pinnedId,
    pageSize,
    localId: input.localId,
  });

  const pageCount = Math.max(1, Math.ceil(ordered.length / pageSize));
  const page = Math.min(Math.max(input.page, 0), pageCount - 1);
  const visible = ordered.slice(page * pageSize, page * pageSize + pageSize);

  const video = new Map<string, MediaLayer>();
  const tiles: TileView[] = visible.map((participant, index) => {
    const role = roleForTile(index, {
      layout: input.layout,
      pinnedId: input.pinnedId,
      activeSpeakerIds: input.activeSpeakerIds,
      visible,
    });
    if (input.videoSuspended || !participant.cameraOn) return { participant, role, layer: null };
    const layer = clampLayer(role, input.profile, input.tileCssWidth, input.devicePixelRatio);
    // Local video is a loopback preview — it is never forwarded back to us.
    if (!participant.isLocal) video.set(participant.id, layer);
    return { participant, role, layer };
  });

  const screenShare =
    !input.videoSuspended && input.screenShareOwnerId !== null && input.layout === 'presentation';

  const audio = audioSubscriptions({
    profile: input.profile,
    localId: input.localId,
    activeSpeakerIds: input.activeSpeakerIds,
    participants: input.participants,
  });

  const videoCost = [...video.values()].reduce((sum, layer) => sum + wireCostBps(layer), 0);
  /*
   * NOBODY DOWNLOADS THEIR OWN SCREEN.
   *
   * `applyPlan` iterates `room.remoteParticipants` and nothing else, so when
   * the presenter is this client there is no subscription to either the share
   * or its soundtrack — the picture comes off the local capture. Billing them
   * anyway put ~1.7 Mbps of downlink that does not exist into the diagnostics
   * card, and the diagnostics card is what somebody reads to decide whether
   * this room is affordable.
   */
  const watchingShare = input.screenShareOwnerId !== null
    && input.screenShareOwnerId !== input.localId;
  const shareCost =
    screenShare && watchingShare ? QUALITY_PROFILES[input.profile].screenShareBitrate * 1.1 : 0;
  /* The share's SOUNDTRACK is subscribed whenever somebody is presenting, not
     only in the presentation layout — `livekitSubscriptions` gates it on the
     owner rather than on `plan.screenShare`, because switching to Gallery must
     not silence a demo. The diagnostics read-out has to bill it the same way or
     it is reporting a plan the client is not on. */
  const shareAudioCost = watchingShare ? SHARE_AUDIO_BITRATE * 1.1 : 0;

  return {
    tiles,
    page,
    pageCount,
    pageSize,
    plan: { video, audio, screenShare },
    estimatedDownlinkBps: Math.round(
      videoCost + shareCost + shareAudioCost + audio.length * AUDIO_WIRE_BPS,
    ),
  };
}
